import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useReference } from "@/lib/reference";
import { ClipboardCheck, Sparkles, UserRoundCheck, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const emptyNeedsAssessment = {
  industry: "", team_size: "", customer_interaction: "", previous_training: "",
  training_focus: "", current_challenge: "", communication_style: "",
  desired_outcome: "", success_vision_3mo: "", venue: "", additional_info: "",
};

const emptyLead = {
  organization_name: "", contact_name: "", position: "", phone: "", email: "",
  industry: "", referral_source: "", lead_source: "Referral", services_interested: [],
  stage: "New Lead", estimated_value: 0, notes: "",
  needs_assessment: { ...emptyNeedsAssessment },
};

const NEEDS_QUESTIONS = [
  { key: "industry", label: "Industry", placeholder: "e.g., Credit Union, Hospitality, Healthcare, Government" },
  { key: "team_size", label: "Team Size", placeholder: "e.g., 12 frontline staff + 3 supervisors" },
  { key: "customer_interaction", label: "Customer Interaction", placeholder: "How does the team interact with customers? In-person tellers, phone support, mixed?" },
  { key: "previous_training", label: "Previous Training", placeholder: "What training have they had? Last time? Any frameworks they already use?" },
  { key: "training_focus", label: "Training Focus Selected", placeholder: "Which CCS pillar or service should this engagement center on?" },
  { key: "current_challenge", label: "Current Challenge", placeholder: "What's the pain they want to solve right now?" },
  { key: "communication_style", label: "Communication Style", placeholder: "Formal vs informal? Hierarchical? Open feedback culture?" },
  { key: "desired_outcome", label: "Desired Outcome", placeholder: "What does success look like to them?" },
  { key: "success_vision_3mo", label: "3-Month Success Vision", placeholder: "Concretely, what's different 3 months after the workshop?" },
  { key: "venue", label: "Venue", placeholder: "On-site? Off-site? Hybrid? Virtual?" },
];

const LeadFormDialog = ({ open, onOpenChange, initial, onSubmit, mode = "create", onConverted }) => {
  const ref = useReference();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => {
    const base = initial || emptyLead;
    return { ...base, needs_assessment: base.needs_assessment || { ...emptyNeedsAssessment } };
  });
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);

  // Re-sync form whenever the dialog is opened with a (possibly different) lead
  useEffect(() => {
    if (open) {
      const base = initial || emptyLead;
      setForm({ ...base, needs_assessment: base.needs_assessment || { ...emptyNeedsAssessment } });
    }
  }, [open, initial]);

  const handleOpenChange = (v) => {
    if (v) {
      const base = initial || emptyLead;
      setForm({ ...base, needs_assessment: base.needs_assessment || { ...emptyNeedsAssessment } });
    }
    onOpenChange(v);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNA = (k, v) => setForm((f) => ({
    ...f, needs_assessment: { ...(f.needs_assessment || emptyNeedsAssessment), [k]: v },
  }));
  const toggleService = (s) => {
    setForm((f) => {
      const list = new Set(f.services_interested || []);
      if (list.has(s)) list.delete(s); else list.add(s);
      return { ...f, services_interested: Array.from(list) };
    });
  };

  const submit = async () => {
    setSaving(true);
    try {
      await onSubmit({ ...form, estimated_value: Number(form.estimated_value || 0) });
      handleOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const naFilled = Object.values(form.needs_assessment || {}).filter((v) => (v || "").trim()).length;

  const alreadyConverted = Boolean(initial?.converted_client_id);

  const handleConvert = async () => {
    if (!initial?.id) return;
    if (!alreadyConverted && !window.confirm(
      `Convert "${form.organization_name}" into a client?\n\nThis will:\n• Create a new client record with all lead details\n• Mark this lead as "Won"\n• Link the two so you always see the origin`
    )) return;
    setConverting(true);
    try {
      const r = await api.post(`/leads/${initial.id}/convert-to-client`);
      const client = r.data?.client;
      const wasAlready = r.data?.already_converted;
      toast.success(wasAlready ? "Client already exists — opening now" : "Client created from lead");
      onConverted && onConverted(client);
      handleOpenChange(false);
      if (client?.id) navigate(`/app/clients?open=${client.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to convert lead");
    } finally {
      setConverting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="lead-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{mode === "create" ? "Add a new lead" : "Edit lead"}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="basics" className="w-full mt-2">
          <TabsList className="bg-[#EBE3D2]/50 rounded-xl">
            <TabsTrigger value="basics" data-testid="lead-tab-basics" className="rounded-lg">Lead Details</TabsTrigger>
            <TabsTrigger value="needs" data-testid="lead-tab-needs" className="rounded-lg">
              <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />
              Needs Assessment
              {naFilled > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center text-[10px] font-bold rounded-full bg-[#C28A2E] text-white w-5 h-5">{naFilled}</span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* TAB 1 — BASICS */}
          <TabsContent value="basics" className="mt-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label className="text-sm">Organization Name *</Label>
                <Input data-testid="lead-org-input" value={form.organization_name} onChange={(e) => setField("organization_name", e.target.value)} placeholder="ACME Credit Union" />
              </div>
              <div className="space-y-2"><Label className="text-sm">Contact Name *</Label><Input data-testid="lead-contact-input" value={form.contact_name} onChange={(e) => setField("contact_name", e.target.value)} /></div>
              <div className="space-y-2"><Label className="text-sm">Position</Label><Input data-testid="lead-position-input" value={form.position} onChange={(e) => setField("position", e.target.value)} /></div>
              <div className="space-y-2"><Label className="text-sm">Email</Label><Input data-testid="lead-email-input" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} /></div>
              <div className="space-y-2"><Label className="text-sm">Phone</Label><Input data-testid="lead-phone-input" value={form.phone} onChange={(e) => setField("phone", e.target.value)} /></div>
              <div className="space-y-2"><Label className="text-sm">Industry</Label><Input data-testid="lead-industry-input" value={form.industry} onChange={(e) => setField("industry", e.target.value)} /></div>
              <div className="space-y-2"><Label className="text-sm">Referral Source</Label><Input data-testid="lead-referral-input" value={form.referral_source} onChange={(e) => setField("referral_source", e.target.value)} /></div>
              <div className="space-y-2">
                <Label className="text-sm">Lead Source</Label>
                <Select value={form.lead_source} onValueChange={(v) => setField("lead_source", v)}>
                  <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ref.lead_sources.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Stage</Label>
                <Select value={form.stage} onValueChange={(v) => setField("stage", v)}>
                  <SelectTrigger data-testid="lead-stage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ref.stages.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label className="text-sm">Estimated Value (USD)</Label><Input data-testid="lead-value-input" type="number" min="0" value={form.estimated_value} onChange={(e) => setField("estimated_value", e.target.value)} /></div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-sm">Services Interested</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 border border-[#EBE3D2] rounded-xl max-h-44 overflow-y-auto">
                  {ref.services.map((s) => (
                    <label key={s} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        data-testid={`service-checkbox-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        checked={(form.services_interested || []).includes(s)}
                        onCheckedChange={() => toggleService(s)}
                      />
                      <span className="text-[#2A1810]">{s}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-sm">Notes</Label>
                <Textarea data-testid="lead-notes-input" value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={3} placeholder="Discovery notes, key insights, next steps…" />
              </div>
            </div>
          </TabsContent>

          {/* TAB 2 — NEEDS ASSESSMENT */}
          <TabsContent value="needs" className="mt-5">
            <div className="ccs-card p-4 mb-5 flex items-start gap-3" style={{ background: "#FDFBF5" }}>
              <Sparkles className="w-4 h-4 text-[#C28A2E] mt-0.5 shrink-0" />
              <div className="text-sm text-[#5D3A1F]">
                <strong>Capture answers from your discovery call.</strong> The AI uses these to write
                a proposal that speaks directly to <em>this</em> organization — not a generic template.
                Fill in as many as you can; you can always come back and add more after a follow-up meeting.
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {NEEDS_QUESTIONS.map((q) => (
                <div key={q.key} className={q.key === "current_challenge" || q.key === "desired_outcome" || q.key === "success_vision_3mo" ? "md:col-span-2 space-y-2" : "space-y-2"}>
                  <Label className="text-sm font-medium text-[#2A1810]">{q.label}</Label>
                  {q.key === "current_challenge" || q.key === "desired_outcome" || q.key === "success_vision_3mo" || q.key === "customer_interaction" || q.key === "previous_training" ? (
                    <Textarea
                      data-testid={`na-${q.key}`}
                      rows={2}
                      value={form.needs_assessment?.[q.key] || ""}
                      onChange={(e) => setNA(q.key, e.target.value)}
                      placeholder={q.placeholder}
                      className="rounded-lg"
                    />
                  ) : (
                    <Input
                      data-testid={`na-${q.key}`}
                      value={form.needs_assessment?.[q.key] || ""}
                      onChange={(e) => setNA(q.key, e.target.value)}
                      placeholder={q.placeholder}
                      className="rounded-lg"
                    />
                  )}
                </div>
              ))}
              <div className="md:col-span-2 space-y-2 pt-3 border-t border-[#EBE3D2]">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-[#2A1810]">Additional context (paste anything)</Label>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#C28A2E] font-bold">Follow-up meeting notes</span>
                </div>
                <Textarea
                  data-testid="na-additional_info"
                  rows={5}
                  value={form.needs_assessment?.additional_info || ""}
                  onChange={(e) => setNA("additional_info", e.target.value)}
                  placeholder="Paste raw discovery call notes, follow-up meeting transcripts, stakeholder quotes, organizational backstory — anything the AI should use to tailor the proposal."
                  className="rounded-lg"
                />
                <div className="text-xs text-[#6B5944]">
                  💡 Tip: After a follow-up meeting where they elaborate, paste the new details here and re-generate the proposal — it will incorporate everything.
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-5 gap-2 flex-col sm:flex-row">
          {mode === "edit" && (
            alreadyConverted ? (
              <Button
                variant="outline"
                onClick={() => { const cid = initial.converted_client_id; handleOpenChange(false); if (cid) navigate(`/app/clients?open=${cid}`); }}
                data-testid="lead-open-client-btn"
                className="border-[#6B8E5A] text-[#6B8E5A] hover:bg-[#6B8E5A]/10 mr-auto"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Open linked client
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={handleConvert}
                disabled={converting || !form.organization_name || !form.contact_name}
                data-testid="lead-convert-to-client-btn"
                className="border-[#C28A2E] text-[#C28A2E] hover:bg-[#C28A2E]/10 mr-auto"
              >
                <UserRoundCheck className="w-4 h-4 mr-1.5" /> {converting ? "Converting…" : "Convert to client"}
              </Button>
            )
          )}
          <Button variant="outline" onClick={() => handleOpenChange(false)} data-testid="lead-form-cancel">Cancel</Button>
          <Button
            onClick={submit} disabled={saving || !form.organization_name || !form.contact_name}
            className="bg-[#5D3A1F] hover:bg-[#4A2E18]"
            data-testid="lead-form-save"
          >{saving ? "Saving…" : (mode === "create" ? "Add lead" : "Save changes")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LeadFormDialog;
